// Health engine. Auto-computes a project's state from three signals and
// surfaces an array of human-readable reasons. Manual override (set via
// the HealthPill on ProjectDetail) supersedes the auto state but does NOT
// hide the auto computation — the tooltip / menu still shows what the
// system thinks.
//
// Signals (in order of precedence):
//   1. project target passed AND not 100% done → forces "off-track"
//   2. time-vs-progress gap                    → >30% off-track · >15% at-risk
//   3. ... future: blocked tasks, stale activity (left as TODOs below)
//
// Unscheduled projects (no startDate / no targetDate) can never be at-risk
// — they show on-track with no reasons.

import { Project, ProjectHealth, Task } from './types';

const DAY = 86400000;

export interface HealthReason {
  kind: 'gap' | 'overdue';
  text: string;
}

export interface HealthInfo {
  /** What the pill should display. Manual override wins over auto. */
  state: ProjectHealth;
  /** What the system *would* show without an override. */
  auto: ProjectHealth;
  /** Owner-pinned state, or null/undefined when on auto. */
  manual: ProjectHealth | null | undefined;
  /** Why the auto state is what it is. Empty when on-track for clean reasons. */
  reasons: HealthReason[];
  /** True when no dates were set; the pill shows on-track but the banner
   *  should explain that this is a "no signal" case, not a real success. */
  isUnscheduled: boolean;
}

function dayDiff(a: number, b: number): number {
  return Math.round((a - b) / DAY);
}

export function computeHealth(
  project: Project,
  tasks: Task[],
  now: number = Date.now(),
): HealthInfo {
  const reasons: HealthReason[] = [];
  const manual = project.manualHealth ?? null;

  // Unscheduled / backlogged projects are inert. We don't want to scream at
  // someone for parking a project before it has dates.
  if (
    typeof project.startDate !== 'number' ||
    typeof project.targetDate !== 'number'
  ) {
    return {
      state: manual || 'on-track',
      auto: 'on-track',
      manual,
      reasons: [],
      isUnscheduled: true,
    };
  }

  // Aggregate task counts for this project. We loop once and bucket so we
  // don't re-scan tasks for every signal.
  let total = 0;
  let done = 0;
  for (const t of tasks) {
    if (t.projectId !== project.id) continue;
    total++;
    if (t.status === 'done') done++;
  }

  const start = project.startDate;
  const target = project.targetDate;
  const totalSpan = Math.max(1, dayDiff(target, start));
  const elapsed = dayDiff(now, start);
  const timePct = Math.max(0, Math.min(1, elapsed / totalSpan));
  const progressPct = total > 0 ? done / total : 0;
  const gap = (timePct - progressPct) * 100; // positive = behind

  let auto: ProjectHealth = 'on-track';

  if (gap > 30) {
    auto = 'off-track';
    reasons.push({
      kind: 'gap',
      text: `${Math.round(gap)}% behind schedule (${Math.round(timePct * 100)}% time elapsed, ${Math.round(progressPct * 100)}% complete)`,
    });
  } else if (gap > 15) {
    auto = 'at-risk';
    reasons.push({
      kind: 'gap',
      text: `${Math.round(gap)}% behind schedule (${Math.round(timePct * 100)}% time elapsed, ${Math.round(progressPct * 100)}% complete)`,
    });
  }

  if (target < now && done < total) {
    auto = 'off-track';
    reasons.push({
      kind: 'overdue',
      text: `Project target passed ${Math.abs(dayDiff(target, now))}d ago`,
    });
  }

  return {
    state: manual || auto,
    auto,
    manual,
    reasons,
    isUnscheduled: false,
  };
}
